import DropdownButton from '@/components/doppel_design/dropdown_button'
import {
  DOPPEL_GREY,
  DOPPEL_LIGHT_GREY,
  DOPPEL_PLACEHOLDER_GREY,
  DOPPEL_TEXT_BLACK,
  DOPPEL_TEXT_BLUE,
  DOPPEL_WHITE,
  FONT_SIZE_MEDIUM,
} from '@/utils/style'
import { HStack, Menu, MenuItem, MenuList, useDisclosure } from '@chakra-ui/react'
import { ReactElement, ReactNode, useEffect, useState, useMemo } from 'react'
import DropdownPaginationButtons from '@/components/doppel_design/paginated_dropdown/dropdown_pagination_buttons'
import SearchInput from '@/components/shared/search_input'
import throttle from 'lodash.throttle'

const DEFAULT_PAGE_SIZE = 10
const DEBOUNCE_INTERVAL = 400 // ms; for when autoTriggerSearch is true

type PaginatedDropdownProps<T> = {
  useFetchDataQuery: (params: {
    searchQuery: string
    limit: number
    offset: number
  }) => {
    items: T[]
    loading: boolean
    refetch: () => void
  }
  useFetchCountQuery: (searchQuery: string) => {
    count: number
    loading: boolean
    refetch: () => void
  }
  selectedItem?: T
  onSelect: (item: T | undefined) => void
  itemToLabel: (item: T) => ReactNode
  itemToKey: (item: T) => string
  menuLabel: string
  isDisabled?: boolean
  pageSize?: number
  searchPlaceholderText?: string
  textColor?: string
  autoTriggerSearch?: boolean
  isInternal?: boolean
}

function PaginatedDropdown<T>({
  useFetchDataQuery,
  useFetchCountQuery,
  selectedItem,
  onSelect,
  itemToLabel,
  itemToKey,
  menuLabel,
  isDisabled = false,
  pageSize = DEFAULT_PAGE_SIZE,
  searchPlaceholderText = 'Search...',
  textColor = DOPPEL_TEXT_BLUE,
  autoTriggerSearch = true,
  isInternal = false,
}: PaginatedDropdownProps<T>): ReactElement {
  const [pageIndex, setPageIndex] = useState(0)
  const [searchInputText, setSearchInputText] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const {
    count: totalCount,
    loading: countLoading,
    refetch: countRefetch,
  } = useFetchCountQuery(searchQuery)
  const { items, loading, refetch } = useFetchDataQuery({
    offset: pageIndex * pageSize,
    limit: pageSize,
    searchQuery,
  })
  const { isOpen, onOpen, onClose } = useDisclosure()

  function handlePageChange(newPage: number) {
    setPageIndex(newPage)
    refetch()
  }

  useEffect(() => {
    handlePageChange(0) // Also calls refetch()
    countRefetch()
  }, [searchQuery])

  const handleSearch = () => {
    if (autoTriggerSearch) {
      // the "search" already happens automatically,
      // so we hijack the listener to select the first item for efficiency
      if (searchQuery.length > 0 && items.length > 0) {
        onSelect(items[0])
        onClose()
      }
    } else {
      // regular searching behavior
      setSearchQuery(searchInputText)
    }
  }

  const throttledSearch = useMemo(() => {
    return throttle(setSearchQuery, DEBOUNCE_INTERVAL, {
      leading: false,
      trailing: true, // ensure final text is searched
    })
  }, []) // empty deps since setSearchQuery is stable

  // Cleanup throttle on unmount
  useEffect(() => {
    return () => throttledSearch.cancel()
  }, [throttledSearch])

  const handleSearchTermChange = (searchKey: string) => {
    setSearchInputText(searchKey)
    if (autoTriggerSearch) {
      throttledSearch(searchKey)
    }
  }

  const renderItem = (item: T) => {
    return (
      <MenuItem
        _hover={{ bg: DOPPEL_GREY }}
        bg={DOPPEL_WHITE}
        borderRadius="md"
        fontSize={FONT_SIZE_MEDIUM}
        fontWeight={500}
        key={itemToKey(item)}
        onClick={() => onSelect(item)}
        textColor={textColor}
        width="100%"
      >
        {itemToLabel(item)}
      </MenuItem>
    )
  }

  const dropdownHeader = (
    <HStack>
      <SearchInput
        _hover={{ bg: DOPPEL_GREY }}
        _placeholder={{ color: DOPPEL_PLACEHOLDER_GREY }}
        bg={DOPPEL_WHITE}
        borderColor={DOPPEL_LIGHT_GREY}
        handleSearch={handleSearch}
        handleSearchTermChange={handleSearchTermChange}
        hideSearchButton
        isOpen={isOpen}
        placeholder={searchPlaceholderText}
        searchTerm={searchQuery}
        textColor={DOPPEL_TEXT_BLACK}
      />

      <DropdownPaginationButtons
        handlePageChange={handlePageChange}
        pageIndex={pageIndex}
        pageSize={pageSize}
        totalCount={totalCount}
      />
    </HStack>
  )

  const menuItems = (
    <MenuList bg={DOPPEL_WHITE} maxHeight="500px" overflow="auto" px={1}>
      {items.map(renderItem)}
    </MenuList>
  )

  return (
    <Menu autoSelect={false} isOpen={isOpen} onClose={onClose} onOpen={onOpen}>
      <DropdownButton
        isDisabled={isDisabled}
        isInternal={isInternal}
        label={selectedItem ? itemToLabel(selectedItem) : menuLabel}
        onClick={() => {
          setSearchInputText('')
          setSearchQuery('')
          setPageIndex(0)
        }}
        useMenuButton
      />

      <MenuList bg={DOPPEL_WHITE} maxHeight="500px" overflow="auto" px={1}>
        {dropdownHeader}

        {menuItems}
      </MenuList>
    </Menu>
  )
}

export default PaginatedDropdown
